/**
 * HTTP client for making requests to the Mastodon API.
 * Handles authentication, error handling, and response parsing.
 */

import { authStorage } from "./storage";
import type { MastodonError } from "./types";

export class ApiError extends Error {
	readonly status: number;
	readonly response?: MastodonError;

	constructor(message: string, status: number, response?: MastodonError) {
		super(message);
		this.name = "ApiError";
		this.status = status;
		this.response = response;
	}

	static async fromResponse(response: Response): Promise<ApiError> {
		let errorData: MastodonError | undefined;
		try {
			errorData = await response.json();
		} catch {
			// Response wasn't JSON
		}
		const message = errorData?.error ?? `HTTP ${response.status}: ${response.statusText}`;
		return new ApiError(message, response.status, errorData);
	}
}

export type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

interface RequestOptions {
	/** Skip adding Authorization header */
	noAuth?: boolean;
	/** Request body - will be JSON stringified if object */
	body?: unknown;
	/** Custom headers */
	headers?: Record<string, string>;
	/** Override the instance domain */
	domain?: string;
	/** Content type - defaults to application/json for object bodies */
	contentType?: string;
}

/**
 * Make an API request to the Mastodon instance
 */
export async function request<T>(
	method: HttpMethod,
	endpoint: string,
	options: RequestOptions = {},
): Promise<T> {
	console.debug("Sending a " + method + " request to " + endpoint, options);
	const domain = options.domain ?? authStorage.instanceDomain;
	if (!domain) {
		throw new ApiError("No instance domain configured", 0);
	}

	const url = `${domain}${endpoint}`;
	const headers: Record<string, string> = { ...options.headers };

	// Add auth header if we have a token and auth isn't disabled
	if (!options.noAuth && authStorage.accessToken) {
		headers["Authorization"] = `Bearer ${authStorage.accessToken}`;
	}

	// Handle body
	let body: BodyInit | undefined;
	if (options.body !== undefined) {
		if (options.body instanceof FormData) {
			body = options.body;
			// Don't set Content-Type for FormData - browser will set it with boundary
		} else if (typeof options.body === "string") {
			body = options.body;
			headers["Content-Type"] = options.contentType ?? "application/json";
		} else {
			body = JSON.stringify(options.body);
			headers["Content-Type"] = options.contentType ?? "application/json";
		}
	}

	const response = await fetch(url, {
		method,
		headers,
		body,
	});

	if (!response.ok) {
		throw await ApiError.fromResponse(response);
	}

	// Handle empty responses
	const text = await response.text();
	if (!text) {
		return undefined as T;
	}

	// console.log("Response text:", text);

	return JSON.parse(text) as T;
}

/**
 * GET request helper
 */
export function get<T>(endpoint: string, options?: RequestOptions): Promise<T> {
	return request<T>("GET", endpoint, options);
}

/**
 * POST request helper
 */
export function post<T>(endpoint: string, body?: unknown, options?: RequestOptions): Promise<T> {
	return request<T>("POST", endpoint, { ...options, body });
}

/**
 * PUT request helper
 */
export function put<T>(endpoint: string, body?: unknown, options?: RequestOptions): Promise<T> {
	return request<T>("PUT", endpoint, { ...options, body });
}

/**
 * PATCH request helper
 */
export function patch<T>(endpoint: string, body?: unknown, options?: RequestOptions): Promise<T> {
	return request<T>("PATCH", endpoint, { ...options, body });
}

/**
 * DELETE request helper
 */
export function del<T>(endpoint: string, options?: RequestOptions): Promise<T> {
	return request<T>("DELETE", endpoint, options);
}

/**
 * Build URL query string from params object
 */
export function buildQuery(params: object): string {
	const searchParams = new URLSearchParams();
	for (const [key, value] of Object.entries(params)) {
		if (value !== undefined && value !== null) {
			if (Array.isArray(value)) {
				value.forEach((v) => searchParams.append(`${key}[]`, String(v)));
			} else {
				searchParams.append(key, String(value));
			}
		}
	}
	const query = searchParams.toString();
	return query ? `?${query}` : "";
}
